import React, { CSSProperties, ReactNode, useState } from 'react';
import DataTableNew from '../../../components/main/DataTableNew/DataTableNew';
import { HealthCheckSortInput, useGetHealthCheckListQuery } from '../../../generated/graphql';
import DeleteHealthChecksDialog from '../../../components/dialogs/DeleteHealthChecksDialog/DeleteHealthChecksDialog';
import NewHealthCheckDialog from '../../../components/dialogs/NewHealthCheckDialog/NewHealthCheckDialog';
import AddHealthChecksTagsDialog from '../../../components/dialogs/AddHealthChecksTagsDialog/AddHealthChecksTagsDialog';
import EditHealthCheckSettingsDialog from '../../../components/dialogs/EditHealthCheckSettingsDialog/EditHealthCheckSettingsDialog';
import EditHealthCheckDialog from '../../../components/dialogs/EditHealthCheckDialog/EditHealthCheckDialog';
import DuplicateHealthCheckDialog from '../../../components/dialogs/DuplicateHealthCheckDialog/DuplicateHealthCheckDialog';
import { Plus, Trash2, Tags, Pencil, Copy, Check, CopyPlus, X, Settings2 } from 'lucide-react';
import RefreshButton from '../../../components/main/RefreshButton/RefreshButton';
import TagBadge from '../../../components/main/TagBadge/TagBadge';
import { createGraphQLOrder, TableSort } from '../../../components/main/DataTableNew/DataTableSorting';

type HealthChecksProps = {
    style?: CSSProperties | undefined,
    children?: ReactNode | undefined,
    className?: string;
};

const HealthCheckDetailPanel = ({ row, onClose, onRefetch }: { row: any; onClose: () => void; onRefetch: () => void }) => {
    const [copied, setCopied] = useState(false);
    const [editVisible, setEditVisible] = useState(false);
    const [duplicateVisible, setDuplicateVisible] = useState(false);
    const [settingsVisible, setSettingsVisible] = useState(false);

    const handleCopyId = () => {
        navigator.clipboard.writeText(row.id);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <div
            className="flex flex-col h-full"
            style={{
                width: '340px',
                borderLeft: '1px solid var(--border-default)',
                backgroundColor: 'var(--bg-card)',
                flexShrink: 0,
            }}
        >
            {/* Header */}
            <div className="flex items-start justify-between px-5 py-4" style={{ borderBottom: '1px solid var(--border-default)' }}>
                <div className="flex-1 min-w-0 pr-2">
                    <p className="text-xs font-medium mb-0.5" style={{ color: 'var(--text-tertiary)' }}>{row.group}</p>
                    <h3 className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{row.name}</h3>
                </div>
                <button className="btn-icon p-1 rounded flex-shrink-0" onClick={onClose}>
                    <X size={15} />
                </button>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1 px-4 py-2.5" style={{ borderBottom: '1px solid var(--border-default)' }}>
                <button className="btn btn-ghost text-xs" onClick={() => setEditVisible(true)}>
                    <Pencil size={13} /> Edit
                </button>
                <button className="btn btn-ghost text-xs" onClick={() => setDuplicateVisible(true)}>
                    <CopyPlus size={13} /> Duplicate
                </button>
                <button className="btn btn-ghost text-xs" onClick={() => setSettingsVisible(true)}>
                    <Settings2 size={13} /> Settings
                </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-5">
                {/* Meta */}
                <div className="grid grid-cols-[auto_1fr] gap-x-6 gap-y-2.5 text-xs">
                    <span style={{ color: 'var(--text-tertiary)' }}>Display</span>
                    <span style={{ color: 'var(--text-primary)' }}>{row.display}</span>
                    <span style={{ color: 'var(--text-tertiary)' }}>Limit</span>
                    <span style={{ color: 'var(--text-primary)' }}>{row.limit}</span>
                    <span style={{ color: 'var(--text-tertiary)' }}>ID</span>
                    <span className="flex items-center gap-1.5 min-w-0">
                        <span className="font-mono truncate" style={{ color: 'var(--text-secondary)' }} title={row.id}>{row.id}</span>
                        <button
                            className="btn-icon p-0.5 rounded flex-shrink-0"
                            onClick={handleCopyId}
                            title={copied ? 'Copied!' : 'Copy ID'}
                            style={{ color: copied ? 'var(--success)' : undefined }}
                        >
                            {copied ? <Check size={12} /> : <Copy size={12} />}
                        </button>
                    </span>
                </div>

                {/* Description */}
                {row.description && (
                    <div>
                        <p className="text-xs font-medium mb-1.5" style={{ color: 'var(--text-tertiary)' }}>Description</p>
                        <p className="text-xs" style={{ color: 'var(--text-secondary)', lineHeight: '1.7', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {row.description}
                        </p>
                    </div>
                )}

                {/* Tags */}
                {row.tags?.length > 0 && (
                    <div>
                        <p className="text-xs font-medium mb-1.5" style={{ color: 'var(--text-tertiary)' }}>Tags</p>
                        <div className="flex flex-wrap gap-1">
                            {row.tags.filter(Boolean).map((t: any) => (
                                <TagBadge key={t.id} tag={t} />
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {editVisible && (
                <EditHealthCheckDialog
                    visible={editVisible}
                    id={row.id}
                    name={row.name}
                    group={row.group}
                    display={row.display}
                    limit={row.limit}
                    description={row.description}
                    onClose={() => { setEditVisible(false); onRefetch(); }}
                />
            )}
            {duplicateVisible && (
                <DuplicateHealthCheckDialog
                    visible={duplicateVisible}
                    sourceId={row.id}
                    name={row.name}
                    group={row.group}
                    display={row.display}
                    limit={row.limit}
                    description={row.description}
                    settings={row.settings}
                    onClose={() => { setDuplicateVisible(false); onRefetch(); }}
                />
            )}
            {settingsVisible && (
                <EditHealthCheckSettingsDialog
                    visible={settingsVisible}
                    id={row.id}
                    settings={row.settings}
                    onClose={() => { setSettingsVisible(false); onRefetch(); }}
                />
            )}
        </div>
    );
};

const HealthChecks = (props: HealthChecksProps) => {
    const [assignHealthChecksTagsVisible, setAssignHealthChecksTagsVisible] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [deleteHealthCheckDialogVisible, setDeleteHealthChecksDialogVisible] = useState(false);
    const [newHealthCheckVisible, setNewHealthCheckVisible] = useState(false);
    const [selectedRow, setSelectedRow] = useState<any | null>(null);
    const [sort, setSort] = useState<TableSort | null>({ field: 'name', direction: 'asc' });

    const result = useGetHealthCheckListQuery({
        variables: {
            contains: searchText,
            order: createGraphQLOrder<HealthCheckSortInput>(sort),
        }
    });

    const handleOnClose = () => {
        setDeleteHealthChecksDialogVisible(false);
        setNewHealthCheckVisible(false);
        setAssignHealthChecksTagsVisible(false);
        result.refetch();
    };

    return (
        <div className="flex flex-col h-full">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b" style={{ borderColor: 'var(--border-default)' }}>
                <button className="btn btn-ghost text-sm" onClick={() => setNewHealthCheckVisible(true)}>
                    <Plus size={15} /> Add
                </button>
                <RefreshButton onRefresh={() => result.refetch()} />
                {selectedIds.length > 0 && (
                    <>
                        <div className="w-px h-5 mx-1" style={{ backgroundColor: 'var(--border-default)' }} />
                        <button className="btn btn-ghost text-sm" onClick={() => setAssignHealthChecksTagsVisible(true)}>
                            <Tags size={15} /> Assign Tags
                        </button>
                        <button className="btn btn-ghost text-sm" style={{ color: 'var(--danger)' }} onClick={() => setDeleteHealthChecksDialogVisible(true)}>
                            <Trash2 size={15} /> Delete
                        </button>
                    </>
                )}
            </div>

            <div className="flex flex-1 overflow-hidden">
                <div className="flex flex-col flex-1 overflow-hidden min-w-0">
                    <DataTableNew
                        isLoading={result.loading}
                        data={result.data?.HealthCheckList?.nodes}
                        columns={[
                            { id: 'group', name: 'Group', width: '160px', sortField: 'group' },
                            { id: 'display', name: 'Display', width: '120px', sortField: 'display' },
                            { id: 'limit', name: 'Limit', width: '80px', sortField: 'limit' },
                        ]}
                        onRowClick={(row) => setSelectedRow((prev: any) => prev?.id === row.id ? null : row)}
                        onSelected={(ids) => setSelectedIds(ids)}
                        onSearchChanged={(value) => setSearchText(value)}
                        sortingMode="server"
                        sort={sort}
                        onSortChange={setSort}
                        fetchMore={() => result.data?.HealthCheckList?.pageInfo?.hasNextPage && result.fetchMore({
                            variables: {
                                contains: searchText,
                                after: result.data?.HealthCheckList?.pageInfo.endCursor,
                                order: createGraphQLOrder<HealthCheckSortInput>(sort),
                            },
                            updateQuery: (previousResult, { fetchMoreResult }) => {
                                if (!fetchMoreResult) return previousResult;
                                const previousHealthChecks = result.data?.HealthCheckList?.nodes || [];
                                const newHealthChecks = fetchMoreResult.HealthCheckList?.nodes || [];
                                return {
                                    ...previousResult,
                                    HealthCheckList: {
                                        nodes: [...previousHealthChecks, ...newHealthChecks],
                                        pageInfo: fetchMoreResult.HealthCheckList?.pageInfo as any
                                    }
                                };
                            }
                        })}
                    />
                </div>

                {selectedRow && (
                    <HealthCheckDetailPanel
                        row={selectedRow}
                        onClose={() => setSelectedRow(null)}
                        onRefetch={() => result.refetch()}
                    />
                )}
            </div>

            {deleteHealthCheckDialogVisible && <DeleteHealthChecksDialog ids={selectedIds} visible={deleteHealthCheckDialogVisible} onClose={handleOnClose} />}
            {newHealthCheckVisible && <NewHealthCheckDialog visible={newHealthCheckVisible} onClose={handleOnClose} />}
            {assignHealthChecksTagsVisible && <AddHealthChecksTagsDialog ids={selectedIds} visible={assignHealthChecksTagsVisible} onClose={handleOnClose} />}
        </div>
    );
};

export default HealthChecks;
